/* PADMĒ Botanics theme script
   Motion values follow the INITS object in the approved prototype. Each section initialises
   itself inside a gsap.context scoped to that section, so the theme editor can reload one
   section at a time (shopify:section:load / unload). */
(() => {
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HAS_GSAP = !!(window.gsap && window.ScrollTrigger);
  if (HAS_GSAP) gsap.registerPlugin(ScrollTrigger);
  const MOTION = HAS_GSAP && !RM;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------- header: shrink on scroll, mobile menu ---------- */
  const updateHeader = () => document.body.classList.toggle('scrolled', window.scrollY > 40);
  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();

  const initMenu = () => {
    const btn = $('.menu-btn'), nav = $('#site-nav');
    if (!btn || !nav || btn.dataset.bound) return;
    btn.dataset.bound = '1';
    const setOpen = open => { nav.classList.toggle('open', open); btn.setAttribute('aria-expanded', String(open)); };
    btn.addEventListener('click', () => setOpen(!nav.classList.contains('open')));
    nav.addEventListener('click', e => { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && nav.classList.contains('open')) { setOpen(false); btn.focus(); }
    });
  };

  /* ---------- refresh scroll positions once media has loaded ---------- */
  let refreshTimer = 0;
  const refreshSoon = () => {
    if (!HAS_GSAP) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { ScrollTrigger.sort(); ScrollTrigger.refresh(); }, 200);
  };
  const watchMedia = root => {
    $$('img', root).forEach(img => { if (!img.complete) img.addEventListener('load', refreshSoon, { once: true }); });
    $$('video', root).forEach(v => v.addEventListener('loadedmetadata', refreshSoon, { once: true }));
  };

  /* split a heading into letters for the write-in reveal */
  const splitLetters = h => {
    if (h.dataset.split) return;
    const txt = h.textContent.trim();
    h.dataset.split = '1';
    h.setAttribute('aria-label', txt);
    h.innerHTML = txt.split(' ').map(w => '<span class="wword" aria-hidden="true">' + Array.from(w).map(ch => '<span class="wch">' + ch + '</span>').join('') + '</span>').join(' ');
  };

  /* the section wrapper Shopify puts around each section */
  const nextSection = el => {
    const wrap = el.closest('.shopify-section');
    const next = wrap && wrap.nextElementSibling;
    return next && next.firstElementChild;
  };

  const INITS = {
    'hero-image'(el) {
      requestAnimationFrame(() => el.classList.add('ready'));
    },

    'floating-card-feature'(el) {
      if (!MOTION) return;
      const card = $('[data-parallax-card]', el), photo = $('[data-parallax-photo]', el);
      const mm = gsap.matchMedia();
      if (el.dataset.layout === 'intro') {
        const st = { trigger: el, start: 'top top', end: 'bottom top', scrub: true };
        mm.add('(min-width: 761px)', () => {
          gsap.fromTo(card, { y: 50 }, { y: -90, ease: 'none', scrollTrigger: st });
          gsap.fromTo(photo, { y: 0 }, { y: 40, ease: 'none', scrollTrigger: { ...st } });
        });
        mm.add('(max-width: 760px)', () => {
          gsap.fromTo(card, { y: 0 }, { y: -36, ease: 'none', scrollTrigger: st });
        });
      } else {
        const st = { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true };
        mm.add('(min-width: 761px)', () => {
          gsap.fromTo(card, { y: 90 }, { y: -90, ease: 'none', scrollTrigger: st });
          gsap.fromTo(photo, { y: -25 }, { y: 25, ease: 'none', scrollTrigger: { ...st } });
        });
        mm.add('(max-width: 760px)', () => {
          gsap.fromTo(card, { y: 36 }, { y: -36, ease: 'none', scrollTrigger: st });
        });
      }
    },

    'floating-text-box'(el) {
      if (!MOTION) return;
      const card = $('[data-parallax-card]', el), photo = $('[data-parallax-photo]', el);
      const st = { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true };
      const mm = gsap.matchMedia();
      mm.add('(min-width: 761px)', () => {
        gsap.fromTo(card, { y: 50 }, { y: -90, ease: 'none', scrollTrigger: st });
        gsap.fromTo(photo, { y: 0 }, { y: 40, ease: 'none', scrollTrigger: { ...st } });
      });
      mm.add('(max-width: 760px)', () => {
        gsap.fromTo(card, { y: 36 }, { y: -36, ease: 'none', scrollTrigger: st });
      });
    },

    'scroll-scrub-video'(el) {
      const video = $('video', el);
      const hand = $('[data-clock-hand]', el), arc = $('[data-clock-arc]', el), label = $('[data-clock-label]', el);
      const total = +el.dataset.minutes || 60;
      const setClock = p => {
        const m = Math.round(p * total);
        hand.setAttribute('transform', 'rotate(' + (p * 360) + ' 32 32)');
        const a = p * 2 * Math.PI, x = 32 + 28 * Math.sin(a), y = 32 - 28 * Math.cos(a);
        arc.setAttribute('d', p <= 0 ? '' : p >= 1 ? 'M32 4 A28 28 0 1 1 31.99 4' : 'M32 4 A28 28 0 ' + (p > .5 ? 1 : 0) + ' 1 ' + x.toFixed(2) + ' ' + y.toFixed(2));
        label.textContent = m === 0 ? el.dataset.labelStart : m >= total ? el.dataset.labelEnd : el.dataset.labelMid.replace('N', m);
      };

      if (!MOTION) {
        // static: show the full hour on the clock and let visitors play the video themselves
        setClock(1);
        if (video) { video.controls = true; video.preload = 'metadata'; video.src = video.dataset.src; }
        return;
      }
      setClock(0);
      if (!video) return;

      // Download the whole file first and play it from memory: seeking then never waits on the
      // network, which is what left the frame blank on slower connections and in Safari.
      const src = video.dataset.src;
      const attach = url => {
        video.preload = 'auto';
        video.src = url;
        video.load();
      };
      fetch(src)
        .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
        .then(blob => { if (el.isConnected) attach(URL.createObjectURL(blob)); })
        .catch(() => attach(src));

      // iOS only decodes frames after the video has played once; start and pause it as soon as it
      // can, and again on the first touch in case Low Power Mode blocked the silent start
      let primed = false;
      const prime = () => {
        if (primed || video.readyState < 1) return;
        const p = video.play();
        if (p && p.then) p.then(() => { primed = true; video.pause(); seek(); }).catch(() => {});
        else { primed = true; video.pause(); }
      };
      video.addEventListener('loadedmetadata', () => { prime(); seek(); refreshSoon(); });
      ['touchstart', 'pointerdown', 'keydown'].forEach(t => window.addEventListener(t, prime, { once: true, passive: true }));

      // Seek to the latest scroll position only once the previous seek has finished, so fast
      // scrolling never piles up seeks the browser cannot keep up with
      let target = 0;
      const seek = () => {
        if (!video.duration || video.seeking) return;
        const t = Math.min(video.duration - .05, target * video.duration);
        if (Math.abs(video.currentTime - t) > .01) { try { video.currentTime = t; } catch (e) {} }
      };
      video.addEventListener('seeked', seek);
      const scrub = p => { target = p; seek(); };
      ScrollTrigger.create({
        trigger: el,
        start: () => (window.innerWidth <= 760 ? 'bottom bottom' : 'top top'),
        end: '+=220%',
        pin: true,
        invalidateOnRefresh: true,
        onUpdate: s => { setClock(s.progress); scrub(s.progress); }
      });
      gsap.from($('.wrap', el), { y: 90, opacity: .4, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 25%', scrub: true } });
    },

    'full-bleed-title'(el) {
      const h = $('[data-letters]', el);
      if (!MOTION) return;
      splitLetters(h);
      gsap.fromTo($$('.wch', h), { opacity: 0, y: 10, filter: 'blur(4px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', ease: 'none', stagger: .05, scrollTrigger: { trigger: el, start: 'top 75%', end: 'top 15%', scrub: .5 } });
      // parallax hand-off: this section drifts down as the next one slides up over it
      const next = nextSection(el);
      if (next) gsap.to(el, { y: () => Math.round(window.innerHeight * .35), ease: 'none', scrollTrigger: { trigger: next, start: 'top bottom', end: 'top top', scrub: true, invalidateOnRefresh: true } });
    },

    'style-viewer-doorways'(el) {
      const btns = $$('[data-style-index]', el), views = $$('[data-style-view]', el);
      btns.forEach(b => b.addEventListener('click', () => {
        const i = b.dataset.styleIndex;
        btns.forEach(x => x.setAttribute('aria-pressed', String(x === b)));
        views.forEach(v => {
          const on = v.dataset.styleView === i;
          v.classList.toggle('on', on);
          v.toggleAttribute('aria-hidden', !on);
        });
      }));
      if (!MOTION) return;
      gsap.from($('[data-slide-up]', el), { y: 140, opacity: .3, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 20%', scrub: true } });
    },

    'text-over-photo'(el) {
      if (!MOTION) return;
      const copy = $('[data-slide-text]', el);
      gsap.from($$('h2, .rte', copy), { x: -120, opacity: 0, ease: 'power2.out', stagger: .12, scrollTrigger: { trigger: copy, start: 'top 85%', end: 'bottom 70%', scrub: .6 } });
      gsap.from($('[data-fade-photo]', el), { opacity: 0, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', end: 'center 60%', scrub: .6 } });
    },

    'process-steps'(el) {
      if (!MOTION) return;
      const mm = gsap.matchMedia();
      mm.add('(min-width: 901px)', () => {
        const stem = $('[data-stem]', el);
        if (!stem) return;
        const len = stem.getTotalLength();
        const rt = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: '+=160%', pin: true, scrub: .6 } });
        rt.fromTo(stem, { strokeDasharray: len, strokeDashoffset: len }, { strokeDashoffset: 0, ease: 'none', duration: 4 }, 0);
        $$('[data-stem-dot]', el).forEach((c, i) => rt.from(c, { scale: 0, transformOrigin: 'center', duration: .3 }, .5 + i));
        $$('.step', el).forEach((s, i) => rt.from(s, { opacity: 0, y: 30, duration: .5 }, .55 + i));
      });
      gsap.from($('[data-slide-up]', el), { y: 90, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 25%', scrub: true } });
    },

    'care-conditions'(el) {
      if (!MOTION) return;
      gsap.from($('[data-care-heading]', el), { y: 40, opacity: 0, duration: .9, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' } });
      // the text slides in from the side opposite the photo; photos stay still
      $$('[data-care-block]', el).forEach(b => {
        const flip = b.dataset.flip === 'true';
        const text = $('[data-care-text]', b);
        const tl = gsap.timeline({ scrollTrigger: { trigger: b, start: 'top 78%', toggleActions: 'play none none reverse' } });
        const parts = Array.from(text.children).flatMap(c => c.classList.contains('rte') ? Array.from(c.children) : [c]).filter(c => !c.classList.contains('lp-facts-wrap'));
        tl.from(parts, { x: flip ? 60 : -60, opacity: 0, stagger: .1, duration: .7, ease: 'power2.out' }, 0);
        const li = $$('li', b);
        if (li.length) tl.from(li, { y: 14, opacity: 0, stagger: .12, duration: .5, ease: 'power2.out' }, '-=.3');
      });
      const trio = $('[data-care-trio]', el);
      if (trio) {
        const cols = Array.from(trio.children);
        const tl = gsap.timeline({ scrollTrigger: { trigger: trio, start: 'top 85%', toggleActions: 'play none none reverse' } });
        tl.fromTo(cols, { '--lw': '0%' }, { '--lw': '100%', duration: .8, stagger: .15, ease: 'power2.inOut' })
          .from(cols.flatMap(c => Array.from(c.children)), { y: 30, opacity: 0, duration: .6, stagger: .08, ease: 'power2.out' }, '-=.6');
      }
    }
  };

  /* ---------- section lifecycle ---------- */
  const contexts = new WeakMap();
  const initSection = el => {
    const type = el.dataset.sectionType;
    if (!INITS[type]) return;
    watchMedia(el);
    if (HAS_GSAP) contexts.set(el, gsap.context(() => INITS[type](el), el));
    else INITS[type](el);
  };
  const destroySection = el => {
    const ctx = contexts.get(el);
    if (ctx) { ctx.revert(); contexts.delete(el); }
  };

  const boot = () => {
    initMenu();
    $$('[data-section-type]').forEach(initSection);
    refreshSoon();
    window.addEventListener('load', refreshSoon);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  // theme editor: re-run a section's script when it is added, changed or removed
  document.addEventListener('shopify:section:load', e => {
    $$('[data-section-type]', e.target).forEach(initSection);
    if (e.target.querySelector('.site-header')) initMenu();
    refreshSoon();
  });
  document.addEventListener('shopify:section:unload', e => {
    $$('[data-section-type]', e.target).forEach(destroySection);
    refreshSoon();
  });
  document.addEventListener('shopify:block:select', e => {
    const b = e.target.closest('[data-style-index]');
    if (b) b.click();
  });
})();
